/**
 * THE TMS MARKING PUSH - one official's report, with the marks it travels with, saved in TMS AS A
 * DRAFT from the Technical Delegate's own browser. RUN 96, 27 September 2026.
 *
 * Authority: the TD's instruction of 27 September 2026 ("push technical officials' reports to TMS
 * as drafts - all officials, TOs, Judges, ATDs, UMs, Umpires - as designed but not built");
 * `09-tms-write-paths.md` section 1 (W2/W3: the marks and the report text for an official, landing
 * in TMS as Draft), section 3 (the exact flow), section 5 (gates 1-6) and section 5.1 (the
 * confirmation, the receipt and every failure branch); `05-reporting-marking.md` section B2 with
 * ruling CC-Q15 (always behave as if `Mark All` is ticked), section B3 (the fixed sets, names
 * verbatim, marks in tens), section B4 (the marks push), section B6 (the report push and its
 * guards) and ruling R1' (ONE record per official for the whole tournament);
 * `12-tms-push-extension.md` (the extension: one guarded read, one POST, a read-back, the TD's
 * Confirm in the extension's own window). The TD's capture of the live marking pages, the same
 * day, is the ground truth for every URL and field below.
 *
 * WHAT TMS DOES, AS CAPTURED (27 September 2026, logged in as the TD)
 *
 *  * `GET {base}/admin/content/competitions/{comp}/marking` - the marking INDEX: a table
 *    "Official | Level | New | Mark | Action", one row per official of the competition, whose
 *    "New" link is `.../mark/create/{tmsPersonId}/{gradingRoleId}`. The grading role is TMS's
 *    (1 was seen for the technical panel, 3 for umpires) and is READ from the link, never assumed.
 *  * `GET .../mark/create/{person}/{role}` - ONE form, `method=post`, `action=.../mark/save`, with
 *    `_token` (Laravel's CSRF token), `all` and `matches[]` (the Match Assignments boxes),
 *    `person_id`, `grading_role_id`, `mark_id` (EMPTY on a create), one range input
 *    `criteria[N]` per criterion (0-100, step 10, N TMS's own criterion id), the textarea `notes`
 *    - where the report goes - and TWO submit buttons named `status`: `draft` ("Save Draft") and
 *    `final` ("Save Final").
 *
 * THE DESIGN IN FOUR RULES
 *
 *  1. DISCOVER, NEVER CONSTRUCT A TARGET. The official's form is found through the index's own
 *     links - the create link, or the edit link of a mark TMS already holds. Nothing here builds a
 *     person or role id into a URL; the only URLs built are the two indexes and the save address,
 *     and every one of them passes {@link guardTmsMarkingRequest}.
 *  2. A SECOND PUSH UPDATES, NEVER DUPLICATES (ruling R1': one record per official). If TMS
 *     already holds a mark for the official - in either competition's index - the push opens THAT
 *     mark's edit form and saves it with its own `mark_id`. A row that shows a mark by a link this
 *     build does not recognise, or a mark column with words but no edit link, or two different
 *     marks, is REFUSED: a create there could be the duplicate this rule exists to prevent.
 *  3. EVERY SLIDER IS OURS, OR THE PUSH DOES NOT GO. The TMS form saves the sliders with the
 *     notes - there is no posting one without the other - so a report push is also a marks push
 *     (09 section 1: W2 carries "the whole-tournament marks and report text"). Each `criteria[N]`
 *     is matched to exactly one criterion of the official's own fixed set BY ITS CAPTION, at push
 *     time - `05-reporting-marking.md` section B3: TMS's display names are the set's names,
 *     verbatim - with the weight in the caption checked where TMS prints one. A slider left
 *     unmatched, a criterion of ours with no slider, a caption that names two, a weight that
 *     differs, or a value outside the slider's own range or step: refused. The plan carries a
 *     COMPLETE set (the API refuses to prepare one with a criterion unmarked or `N/A`), so a
 *     form default is never what lands - a push that would write 50 (or 0) into a slider nobody
 *     moved is refused, not sent.
 *  4. DRAFT, ONLY EVER. The body is built by hand, control by control in page order, exactly as
 *     the browser would send it with "Save Draft" pressed - `status=draft` and never the `final`
 *     button - and {@link assertDraftOnly} refuses, before any post, a body that is anything else.
 *     No URL naming publish, final, delete or destroy can be requested at all.
 *
 * Everything else is the appointments push's discipline, unchanged: read the pages fresh, plan,
 * the caller's last word (`beforePost` - the extension's hold), POST exactly once and never
 * retry, then read back (the index for TMS's words, which takes any flash, then the mark's own
 * edit form for its values) and believe only the read-back. A form this reader cannot reproduce
 * with certainty is refused before anything is sent.
 *
 * WHERE IT RUNS: the extension's confirm page (a page, not the worker, so it cannot be stopped
 * mid-save), the API's tests and the Control Centre's - no DOM, no network, no clock but the one
 * passed in. Nothing here may be logged whole: a form carries a live CSRF token, and every string
 * that leaves goes through the same redactor the day push uses.
 */
import { asciiLower, attributeOf, collapseWhitespace, encodeFormUrlEncoded, readHtmlPage, } from "./tms-html-form.js";
import { Redactor, TMS_TECHNICAL_COMPETITION_IDS, cleanTmsText, readTmsPageMessages, requirePinnedOrigin, tmsHttpResponseProblem, tmsNameMatches, } from "./tms-technical-day.js";
import { TMS_PUSH_FAILURE_WORDS, isTmsPushExtensionVersion, isTmsPushPressId } from "./tms-push-result.js";
/* -- Constants ------------------------------------------------------------------------------ */
/** The competitions whose marking indexes are read, in this order (09 section 2: no others). */
export const TMS_MARKING_COMPETITION_IDS = TMS_TECHNICAL_COMPETITION_IDS;
/** The one `status` this build ever sends. `final` is TMS's own "Save Final", and a human act. */
export const TMS_MARK_DRAFT_STATUS = "draft";
/** Words no requested address may carry, in any case or encoding. */
export const TMS_MARKING_FORBIDDEN_WORDS = ["publish", "final", "delete", "destroy"];
export const TMS_MARKING_LIMITS = {
    detail: 2000,
    collateral: 300,
    collateralCount: 100,
    fragment: 160,
    /** The report text, as a result carries it back. The API prepares at most 800. */
    notes: 5000,
    criteria: 20,
    matches: 200,
};
/** The plan's roles: every official the TD reports on (the TD himself is never a subject). */
export const TMS_MARKING_SUBJECT_ROLES = ["ATD", "TO", "Judge", "UM", "Umpire"];
/* -- The URLs, and the guard --------------------------------------------------------------- */
function competitionDigits(competitionId, where) {
    if (typeof competitionId !== "string" || !/^[0-9]{1,9}$/.test(competitionId)) {
        throw new Error(`${where}: the competition id must be digits only.`);
    }
    return competitionId;
}
function bareOrigin(base, where) {
    if (typeof base !== "string" || !/^https:\/\/[A-Za-z0-9.-]+(?::[0-9]{1,5})?\/?$/.test(base)) {
        throw new Error(`${where}: the TMS base must be a bare https origin.`);
    }
    return base.endsWith("/") ? base.slice(0, -1) : base;
}
/** `{base}/admin/content/competitions/{comp}/marking` - the marking index. */
export function tmsMarkingIndexUrl(base, competitionId) {
    return `${bareOrigin(base, "tmsMarkingIndexUrl")}/admin/content/competitions/${competitionDigits(competitionId, "tmsMarkingIndexUrl")}/marking`;
}
/** `{base}/admin/content/competitions/{comp}/mark/save` - where the mark form posts. */
export function tmsMarkSaveUrl(base, competitionId) {
    return `${bareOrigin(base, "tmsMarkSaveUrl")}/admin/content/competitions/${competitionDigits(competitionId, "tmsMarkSaveUrl")}/mark/save`;
}
export class TmsMarkingRequestRefusedError extends Error {
    constructor(message) {
        super(message);
        this.name = "TmsMarkingRequestRefusedError";
    }
}
const COMPETITION_PREFIX = "^\\/admin\\/content\\/competitions\\/([0-9]+)";
const INDEX_PATH = new RegExp(`${COMPETITION_PREFIX}\\/marking$`);
const CREATE_PATH = new RegExp(`${COMPETITION_PREFIX}\\/mark\\/create\\/([1-9][0-9]{0,11})\\/([1-9][0-9]{0,5})$`);
/**
 * The edit shapes a Laravel admin writes — and the one TMS ACTUALLY writes, learned in the
 * field: the second live push (27 Sep 2026, updating SINGH Jaswant's mark 5612) was refused
 * because the real index links an existing mark as plain `/mark/{id}`, with no `edit` in the
 * path at all. That page is the edit form (Save Draft / Save Final / Delete on it), so the
 * bare-id shape is recognised alongside the two guessed ones. It is listed LAST: `create` and
 * `save` are matched by their own patterns before this one can swallow their words, and the
 * bare pattern's digits cannot match either (`create`/`save` are not digits). Any other shape
 * of mark link is refused, loudly, as before.
 */
const EDIT_PATHS = [
    new RegExp(`${COMPETITION_PREFIX}\\/mark\\/edit\\/([1-9][0-9]{0,11})$`),
    new RegExp(`${COMPETITION_PREFIX}\\/mark\\/([1-9][0-9]{0,11})\\/edit$`),
    new RegExp(`${COMPETITION_PREFIX}\\/mark\\/([1-9][0-9]{0,11})$`),
];
const SAVE_PATH = new RegExp(`${COMPETITION_PREFIX}\\/mark\\/save$`);
function markingCompetition(value) {
    return TMS_MARKING_COMPETITION_IDS.find((id) => id === value) ?? null;
}
/**
 * The allow-list every marking request passes, here and again in every transport. EXACTLY:
 *
 *   GET  {base}/admin/content/competitions/{1916|1917}/marking
 *   GET  {base}/admin/content/competitions/{1916|1917}/mark/create/{person}/{role}
 *   GET  {base}/admin/content/competitions/{1916|1917}/mark/edit/{mark}   (or .../mark/{mark}/edit)
 *   POST {base}/admin/content/competitions/{1916|1917}/mark/save
 *
 * on the pinned origin, no query, no fragment, no trailing slash; and any address mentioning
 * publish, final, delete or destroy - in any case, percent-encoded or not - refused by name first.
 */
export function guardTmsMarkingRequest(method, url, base, options = {}) {
    const origin = requirePinnedOrigin(base, options, "guardTmsMarkingRequest");
    const refuse = (why) => {
        throw new TmsMarkingRequestRefusedError(`Refused ${cleanTmsText(String(method))} ${cleanTmsText(String(url))}: ${why}. The marking push makes only GET requests for a ` +
            `competition's marking index and one official's mark form, and one POST to ${origin}/admin/content/competitions/{1916|1917}/mark/save.`);
    };
    if (typeof url !== "string" || typeof method !== "string")
        return refuse("it is not a method and an address");
    if (url.length > 2048)
        return refuse("its address is too long");
    let decoded;
    try {
        decoded = decodeURIComponent(url);
    }
    catch {
        return refuse("its address is not well-formed");
    }
    const lowered = asciiLower(decoded);
    const forbidden = TMS_MARKING_FORBIDDEN_WORDS.find((word) => lowered.includes(word));
    if (forbidden !== undefined) {
        return refuse(`it mentions "${forbidden}". Publishing and finalising stay deliberate acts inside TMS, and this build never requests such an address`);
    }
    const match = /^https:\/\/([^/?#]*)(\/[^?#]*)$/.exec(url);
    if (match === null)
        return refuse("it is not an https address without a query or a fragment");
    if (asciiLower(`https://${match[1] ?? ""}`) !== asciiLower(origin))
        return refuse(`it is not on the pinned TMS origin ${origin}`);
    const path = match[2] ?? "";
    const competition = (raw) => markingCompetition(raw) ?? refuse(`competition ${raw ?? ""} is not 1916 or 1917`);
    const onlyGet = (what) => {
        if (method !== "GET")
            refuse(`${what} is only ever read, with GET`);
    };
    const index = INDEX_PATH.exec(path);
    if (index !== null) {
        onlyGet("a marking index");
        return { kind: "marking_index", method: "GET", url, competitionId: competition(index[1]) };
    }
    const create = CREATE_PATH.exec(path);
    if (create !== null) {
        onlyGet("a mark's create form");
        return { kind: "mark_create", method: "GET", url, competitionId: competition(create[1]), tmsPersonId: create[2] ?? "", gradingRoleId: create[3] ?? "" };
    }
    for (const pattern of EDIT_PATHS) {
        const edit = pattern.exec(path);
        if (edit !== null) {
            onlyGet("a mark's edit form");
            return { kind: "mark_edit", method: "GET", url, competitionId: competition(edit[1]), markId: edit[2] ?? "" };
        }
    }
    const save = SAVE_PATH.exec(path);
    if (save !== null) {
        if (method !== "POST")
            return refuse("the mark form is only ever posted, with POST");
        return { kind: "mark_save", method: "POST", url, competitionId: competition(save[1]) };
    }
    return refuse("it is none of the requests the marking push makes");
}
/** A link's address made absolute on `origin` - or null when it points anywhere else. */
function resolveLink(href, origin) {
    const trimmed = href.trim();
    if (/^https?:\/\//i.test(trimmed)) {
        return asciiLower(trimmed).startsWith(`${asciiLower(origin)}/`) ? `${origin}${trimmed.slice(origin.length)}` : null;
    }
    if (trimmed.startsWith("/") && !trimmed.startsWith("//"))
        return `${origin}${trimmed}`;
    return null;
}
function linkPath(href) {
    const withoutOrigin = href.trim().replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/?#]*/, "");
    let decoded = withoutOrigin;
    try {
        decoded = decodeURIComponent(withoutOrigin);
    }
    catch {
        // kept as written
    }
    return decoded;
}
/** A link that points at a mark: anything in the `/mark/` space other than the index itself. */
function isMarkishPath(path) {
    return /\/mark(\/|$)/i.test(path);
}
/* -- Words ------------------------------------------------------------------------------------ */
function fragment(text) {
    return cleanTmsText(text, TMS_MARKING_LIMITS.fragment);
}
function errorMessage(error) {
    try {
        const text = error instanceof Error ? error.message : String(error);
        return text === "" ? "no reason given" : text;
    }
    catch {
        return "an error that could not be described";
    }
}
function quoted(texts) {
    const shown = texts.slice(0, 3).map((text) => `"${text}"`);
    return `${shown.join(" and ")}${texts.length > 3 ? ` and ${String(texts.length - 3)} more` : ""}`;
}
/** What the TD does when a push is refused before anything was sent. */
export const TMS_MARKING_FALLBACK = "Use Copy For TMS or Download Word in the Control Centre and put the report into TMS yourself.";
function adviceFor(reason) {
    switch (reason) {
        case "session_expired":
            return "Sign in to TMS again in this browser, then press Push again.";
        case "unreachable":
            return "Check the connection to TMS, then press Push again.";
        case "form_changed":
            return `${TMS_MARKING_FALLBACK} Tell the build team that TMS's marking page has changed.`;
        case "rejected_by_tms":
            return `Check the official's marking in TMS, then press Push again - or ${TMS_MARKING_FALLBACK.charAt(0).toLowerCase()}${TMS_MARKING_FALLBACK.slice(1)}`;
        case "transport_unavailable":
            return `Press Push again; if it persists, ${TMS_MARKING_FALLBACK.charAt(0).toLowerCase()}${TMS_MARKING_FALLBACK.slice(1)}`;
    }
}
/** Criterion words: letters and digits only, lower case - "Rules & Regulations" = "Rules&Regulations". */
function criterionWords(text) {
    return String(text).normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}
/** The weight a caption prints in brackets - "Rules & Regulations (35)" - or null. */
function captionWeight(caption) {
    const all = [...caption.matchAll(/\((\d{1,3})\)/g)];
    const last = all[all.length - 1];
    return last === undefined ? null : Number(last[1]);
}
function refused(reason, detail, advice = null) {
    return { kind: "refused", reason, detail: `${detail} Nothing was sent. ${advice ?? adviceFor(reason)}` };
}
/** Status, address and sign-in: what every GET answer must pass before its markup is read. */
function answerProblem(response, what, expectedPath) {
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null)
        return refused("transport_unavailable", `The TMS Push transport gave a malformed answer for ${what} (${malformed}).`);
    if (response.status === 0 || (response.status >= 300 && response.status < 400)) {
        return refused("session_expired", `TMS redirected ${what} instead of showing it (the redirect was not followed) - almost always to its sign-in page, so the TMS session has most likely ended.`);
    }
    const path = linkPath(response.url).replace(/[?#].*$/, "");
    const lowered = asciiLower(path);
    if (/(^|\/)(login|signin|sign-in)(\/|$)/.test(lowered) || response.status === 401 || response.status === 419) {
        return refused("session_expired", `TMS answered ${what} with its sign-in page, so the TMS session has ended.`);
    }
    if (response.status === 404)
        return refused("form_changed", `TMS has no page at the address of ${what} (HTTP 404).`);
    if (response.status === 403)
        return refused("rejected_by_tms", `TMS refused to show ${what} (HTTP 403): this TMS account may not mark this official.`);
    if (response.status < 200 || response.status >= 300)
        return refused("rejected_by_tms", `TMS answered HTTP ${String(response.status)} when ${what} was read.`);
    if (response.redirected && path !== expectedPath) {
        return refused("form_changed", `TMS answered ${what} from ${fragment(path || "an unknown address")}, not from the page asked for.`);
    }
    return null;
}
/** The markup, read strictly - or why it cannot be. */
function readPage(html, what) {
    const page = readHtmlPage(html);
    const problem = page.problems[0];
    if (problem !== undefined)
        return refused("form_changed", `${what} could not be read with certainty: the page ${problem}.`);
    if (page.passwordInputs > 0)
        return refused("session_expired", `TMS answered ${what} with a sign-in form, so the TMS session has ended.`);
    if (page.linksTruncated)
        return refused("form_changed", `${what} carries more links than this build reads, so it cannot be read in full.`);
    return { page, messages: readTmsPageMessages(html) };
}
/** The header names of a table's columns, from its first row naming both "Official" and "Mark". */
function headerColumns(page, table) {
    const rows = page.tables[table]?.rows ?? [];
    for (const row of rows) {
        const names = row.cells.map((cell) => asciiLower(collapseWhitespace(cell.text)));
        if (names.includes("official") && names.includes("mark"))
            return new Map(row.cells.map((cell) => [cell.column, asciiLower(collapseWhitespace(cell.text))]));
    }
    return null;
}
/**
 * Read a competition's marking index ANSWER: who is listed, with which create link, and which
 * marks TMS already holds for each - from the links alone, each passed through the guard.
 */
export function readMarkingIndexResponse(response, competitionId, base, options = {}) {
    const what = `the marking index of TMS competition ${competitionId}`;
    const origin = requirePinnedOrigin(base, options, "readMarkingIndexResponse");
    const answered = answerProblem(response, what, `/admin/content/competitions/${competitionId}/marking`);
    if (answered !== null)
        return answered;
    const read = readPage(response.body, what);
    if ("kind" in read)
        return read;
    const { page, messages } = read;
    const rows = new Map();
    const stray = [];
    const loose = [];
    for (const link of page.links) {
        const path = linkPath(link.href);
        const resolved = link.certain ? resolveLink(link.href, origin) : null;
        let guarded = null;
        if (resolved !== null) {
            try {
                guarded = guardTmsMarkingRequest("GET", resolved, origin, options);
            }
            catch {
                guarded = null;
            }
        }
        let row;
        if (link.row !== null) {
            const key = `${String(link.row.table)}:${String(link.row.index)}`;
            row = rows.get(key);
            if (row === undefined) {
                const header = headerColumns(page, link.row.table);
                const texts = link.row.cells.map((cell) => cell.text).filter((text) => text !== "");
                const markCells = header === null ? null : link.row.cells.filter((cell) => ["mark", "action"].includes(header.get(cell.column) ?? ""));
                row = {
                    creates: [],
                    marks: [],
                    unknown: [],
                    rowText: cleanTmsText(texts.join(" | "), 400),
                    markText: markCells === null ? null : cleanTmsText(markCells.map((cell) => cell.text).join(" "), 200),
                };
                rows.set(key, row);
            }
        }
        if (guarded?.kind === "mark_create" && guarded.competitionId === competitionId) {
            const entry = { url: guarded.url, tmsPersonId: guarded.tmsPersonId, gradingRoleId: guarded.gradingRoleId };
            if (row !== undefined)
                row.creates.push(entry);
            else
                loose.push({ creates: [entry], marks: [], unknown: [], rowText: cleanTmsText(link.text, 400), markText: null });
            continue;
        }
        if (guarded?.kind === "mark_edit" && guarded.competitionId === competitionId) {
            if (row !== undefined)
                row.marks.push({ markId: guarded.markId, url: guarded.url });
            else
                stray.push(fragment(path));
            continue;
        }
        if (guarded?.kind === "marking_index")
            continue;
        if (isMarkishPath(path) || asciiLower(path).includes("/mark/")) {
            // Anything else in the mark space - a view link, a report, an edit link in another shape.
            if (row !== undefined)
                row.unknown.push(fragment(path));
            else
                stray.push(fragment(path));
        }
    }
    const entries = [];
    for (const row of [...rows.values(), ...loose]) {
        if (row.creates.length === 0) {
            stray.push(...row.marks.map((mark) => fragment(linkPath(mark.url))), ...row.unknown);
            continue;
        }
        const people = new Set(row.creates.map((create) => `${create.tmsPersonId}/${create.gradingRoleId}`));
        const first = row.creates[0];
        const marks = [...new Map(row.marks.map((mark) => [mark.markId, mark])).values()];
        entries.push({
            competitionId,
            tmsPersonId: first.tmsPersonId,
            gradingRoleId: first.gradingRoleId,
            createUrl: first.url,
            marks,
            unknownMarkLinks: row.unknown,
            rowText: row.rowText,
            markText: row.markText,
            problem: people.size > 1 ? `the row carries create links for ${String(people.size)} different officials or roles` : null,
        });
    }
    return { kind: "index", competitionId, entries, strayMarkLinks: [...new Set(stray)], messages };
}
function numberAttribute(control, name, fallback) {
    const attribute = attributeOf(control, name);
    if (attribute === undefined)
        return fallback;
    const value = attribute.value.trim();
    if (!/^-?[0-9]+(\.[0-9]+)?$/.test(value))
        return null;
    return Number(value);
}
function captionOf(page, control, previousTextIndex) {
    const id = attributeOf(control, "id")?.value;
    const byFor = id === undefined || id === "" ? undefined : page.labels.find((label) => label.forId === id);
    if (byFor !== undefined && byFor.text !== "")
        return byFor.text;
    const wrapping = page.labels.find((label) => label.controls.includes(control.index));
    if (wrapping !== undefined && wrapping.text !== "")
        return wrapping.text;
    if (control.row !== null) {
        const before = control.row.cells.filter((cell) => cell.column < (control.column ?? 0)).map((cell) => cell.text).join(" ");
        if (before.trim() !== "")
            return before;
    }
    return page.text.slice(previousTextIndex, control.textIndex).join(" ");
}
function isSubmit(control) {
    return (control.tag === "button" && control.type === "submit") || (control.tag === "input" && (control.type === "submit" || control.type === "image"));
}
/**
 * Read a mark form ANSWER strictly: exactly one form posting to this competition's save address,
 * carrying exactly the captured fields and nothing else, for exactly the expected official, role
 * and mark. Anything this reader cannot reproduce with certainty refuses the push.
 */
export function readMarkFormResponse(response, expected, options = {}) {
    const what = expected.markId === "" ? `the new-mark form for TMS person ${expected.tmsPersonId} in competition ${expected.competitionId}` : `the form of mark ${expected.markId} in competition ${expected.competitionId}`;
    const origin = requirePinnedOrigin(expected.base, options, "readMarkFormResponse");
    const expectedPath = linkPath(expected.formUrl).replace(/[?#].*$/, "");
    const answered = answerProblem(response, what, expectedPath);
    if (answered !== null)
        return answered;
    const read = readPage(response.body, what);
    if ("kind" in read)
        return read;
    const { page, messages } = read;
    const changed = (why) => refused("form_changed", `${what} is not the form this build knows: ${why}.`);
    const saveUrl = tmsMarkSaveUrl(origin, expected.competitionId);
    const forms = page.forms.filter((form) => !form.inTemplate && form.action !== null && resolveLink(form.action, origin) === saveUrl);
    if (forms.length !== 1)
        return changed(`it has ${String(forms.length)} forms posting to ${saveUrl}, not exactly one`);
    const form = forms[0];
    if (form.method !== "post")
        return changed("its form is not posted with POST");
    if (form.enctype !== "application/x-www-form-urlencoded")
        return changed(`its form is sent as ${form.enctype}`);
    if (form.acceptCharset !== null && !/^\s*utf-?8\s*$/i.test(form.acceptCharset))
        return changed("its form names another character set");
    const owned = page.controls.filter((control) => control.form === form && !control.inTemplate);
    for (const control of owned) {
        const problem = control.problems[0];
        if (problem !== undefined)
            return changed(`the ${control.tag} "${fragment(control.name)}" ${problem}`);
        if (attributeOf(control, "name")?.certain === false)
            return changed("a field's name holds a character reference this reader cannot decode for certain");
    }
    const hidden = { _token: [], person_id: [], grading_role_id: [], mark_id: [] };
    const criteria = [];
    const matches = [];
    let all = { present: false, value: "on", checked: false };
    const notes = [];
    let drafts = 0;
    let previousCaptionEnd = 0;
    for (const control of owned) {
        if (control.inDatalist)
            continue;
        const name = control.name;
        const value = attributeOf(control, "value");
        if (value !== undefined && !value.certain)
            return changed(`the field "${fragment(name)}" has a value holding a character reference this reader cannot decode for certain`);
        if (isSubmit(control)) {
            if (control.type === "image")
                return changed("it has an image submit button");
            if (name !== "status")
                return changed(`it has a submit button${name === "" ? " with no name" : ` named "${fragment(name)}"`} besides Save Draft and Save Final`);
            for (const override of ["formaction", "formmethod", "formenctype", "formtarget"]) {
                if (attributeOf(control, override) !== undefined)
                    return changed(`a status button carries ${override}`);
            }
            const status = value?.value;
            if (status === TMS_MARK_DRAFT_STATUS) {
                if (control.disabled)
                    return changed("its Save Draft button is disabled");
                drafts += 1;
            }
            else if (status !== "final") {
                return changed(`it has a status button sending "${fragment(String(status))}", which is neither draft nor final`);
            }
            continue;
        }
        if (name === "")
            continue; // an unnamed control is never sent
        if (control.tag === "button")
            continue; // type=button or reset: never sent
        if (control.tag === "input" && (control.type === "reset" || control.type === "button"))
            continue;
        if (name in hidden) {
            if (control.tag !== "input" || control.type !== "hidden")
                return changed(`"${name}" is not a hidden field`);
            if (control.disabled)
                return changed(`"${name}" is disabled`);
            hidden[name].push(value?.value ?? "");
            continue;
        }
        if (name === "all") {
            if (control.tag !== "input" || control.type !== "checkbox")
                return changed(`"all" is not a checkbox`);
            if (all.present)
                return changed(`it has two "all" boxes`);
            all = { present: !control.disabled, value: value?.value ?? "on", checked: control.checked };
            continue;
        }
        if (name === "matches[]") {
            if (control.tag !== "input" || control.type !== "checkbox")
                return changed(`a "matches[]" field is not a checkbox`);
            const id = value?.value ?? "on";
            if (!/^[1-9][0-9]{0,15}$/.test(id))
                return changed(`a match box carries "${fragment(id)}", not a TMS match id`);
            const rowWords = control.row === null ? "" : control.row.cells.map((cell) => cell.text).filter((text) => text !== "").join(" | ");
            matches.push({ value: id, label: cleanTmsText(rowWords === "" ? `TMS match ${id}` : rowWords, 200), checked: control.checked, disabled: control.disabled });
            continue;
        }
        const criterion = /^criteria\[([1-9][0-9]{0,5})\]$/.exec(name);
        if (criterion !== null) {
            if (control.tag !== "input" || control.type !== "range")
                return changed(`"${name}" is not a slider`);
            if (control.disabled)
                return changed(`the slider "${name}" is disabled, so its mark cannot be sent`);
            const min = numberAttribute(control, "min", 0);
            const max = numberAttribute(control, "max", 100);
            const stepAttribute = attributeOf(control, "step");
            const step = stepAttribute !== undefined && asciiLower(stepAttribute.value.trim()) === "any" ? null : numberAttribute(control, "step", 1);
            if (min === null || max === null || step === null || step <= 0 || min > max)
                return changed(`the slider "${name}" has a range this build does not read`);
            const caption = cleanTmsText(captionOf(page, control, previousCaptionEnd), 200);
            previousCaptionEnd = control.textIndex;
            criteria.push({ tmsCriterionId: criterion[1] ?? "", caption, weight: captionWeight(caption), value: value?.value ?? null, min, max, step });
            continue;
        }
        if (name === "notes") {
            if (control.tag !== "textarea")
                return changed(`"notes" is not a text box`);
            if (control.disabled)
                return changed(`its Notes box is disabled`);
            if (asciiLower(attributeOf(control, "wrap")?.value ?? "") === "hard")
                return changed("its Notes box wraps hard, which inserts line breaks");
            if (attributeOf(control, "dirname") !== undefined)
                return changed("its Notes box carries dirname, which adds a field");
            notes.push(control.text);
            continue;
        }
        return changed(`it carries a field this build does not know, "${fragment(name)}" - it would be sent unexamined`);
    }
    for (const [name, values] of Object.entries(hidden)) {
        if (values.length !== 1)
            return changed(`it carries ${String(values.length)} "${name}" fields, not exactly one`);
    }
    const token = hidden["_token"]?.[0] ?? "";
    if (token.trim() === "")
        return changed("its security token (_token) is empty");
    if (drafts !== 1)
        return changed(`it has ${String(drafts)} Save Draft buttons, not exactly one`);
    if (notes.length !== 1)
        return changed(`it has ${String(notes.length)} Notes boxes, not exactly one`);
    if (criteria.length === 0)
        return changed("it has no criterion sliders");
    if (new Set(criteria.map((entry) => entry.tmsCriterionId)).size !== criteria.length)
        return changed("it names one criterion twice");
    if (new Set(matches.map((entry) => entry.value)).size !== matches.length)
        return changed("it names one match twice");
    const personId = hidden["person_id"]?.[0] ?? "";
    const gradingRoleId = hidden["grading_role_id"]?.[0] ?? "";
    const markId = hidden["mark_id"]?.[0] ?? "";
    if (personId !== expected.tmsPersonId)
        return changed(`it is for TMS person "${fragment(personId)}", not ${expected.tmsPersonId}`);
    if (gradingRoleId !== expected.gradingRoleId)
        return changed(`it is for grading role "${fragment(gradingRoleId)}", not ${expected.gradingRoleId} as the marking index said`);
    if (markId !== expected.markId) {
        return changed(expected.markId === "" ? `it already carries mark ${fragment(markId)}, so saving it would not make a new mark` : `it carries mark "${fragment(markId)}", not ${expected.markId}`);
    }
    return {
        kind: "form",
        read: {
            token,
            controls: owned,
            form: {
                competitionId: expected.competitionId,
                url: response.url,
                personId,
                gradingRoleId,
                markId,
                criteria,
                matches,
                all,
                notes: notes[0] ?? "",
                messages,
            },
        },
    };
}
/** Every line break as CR LF - the standard's rule for a submitted value. */
function crlf(value) {
    return value.replace(/\r\n|\r|\n/g, "\r\n");
}
/**
 * THE DRAFT-ONLY INVARIANT, checked on the very entries that become the body: exactly one
 * `status`, and it is `draft`. Throws otherwise - a defect, and never a post.
 */
export function assertDraftOnly(entries) {
    const statuses = entries.filter((entry) => entry.name === "status");
    if (statuses.length !== 1 || statuses[0]?.value !== TMS_MARK_DRAFT_STATUS) {
        throw new Error(`The mark form body would send status ${statuses.map((entry) => `"${entry.value}"`).join(", ") || "none"}: this build sends "draft" and nothing else.`);
    }
    if (entries.some((entry) => asciiLower(entry.value) === "final" && asciiLower(entry.name) === "status")) {
        throw new Error("The mark form body would press Save Final: this build never does.");
    }
}
/**
 * Match each slider to exactly one criterion of the plan's set by its caption, and each
 * criterion to exactly one slider - or refuse. Weights printed in a caption must agree.
 */
function matchCriteria(form, plan) {
    const changed = (why) => refused("form_changed", `The TMS marking form for ${plan.subject.mediaName} does not match this official's criteria: ${why}.`);
    const claimed = new Map();
    const planned = [];
    for (const slider of form.criteria) {
        const words = criterionWords(slider.caption);
        const candidates = plan.criteria.filter((criterion) => {
            const name = criterionWords(criterion.name);
            return name !== "" && words.includes(name);
        });
        if (candidates.length === 0)
            return changed(`the slider criteria[${slider.tmsCriterionId}] ("${slider.caption}") names none of them`);
        if (candidates.length > 1)
            return changed(`the slider criteria[${slider.tmsCriterionId}] ("${slider.caption}") could be ${candidates.map((entry) => `"${entry.name}"`).join(" or ")}`);
        const criterion = candidates[0];
        const other = claimed.get(criterion.criterionStableKey);
        if (other !== undefined)
            return changed(`"${criterion.name}" is named by two sliders, criteria[${other}] and criteria[${slider.tmsCriterionId}]`);
        claimed.set(criterion.criterionStableKey, slider.tmsCriterionId);
        if (slider.weight !== null && slider.weight !== criterion.weight) {
            return changed(`TMS weighs "${criterion.name}" at ${String(slider.weight)}, the Control Centre at ${String(criterion.weight)}`);
        }
        const value = criterion.value;
        if (value < slider.min || value > slider.max || Math.abs((value - slider.min) / slider.step - Math.round((value - slider.min) / slider.step)) > 1e-9) {
            return changed(`the mark ${String(value)} for "${criterion.name}" is not a value TMS's slider takes (${String(slider.min)} to ${String(slider.max)} in steps of ${String(slider.step)})`);
        }
        planned.push({
            tmsCriterionId: slider.tmsCriterionId,
            caption: slider.caption,
            criterionStableKey: criterion.criterionStableKey,
            name: criterion.name,
            weight: criterion.weight,
            now: slider.value,
            sent: value,
        });
    }
    const missing = plan.criteria.filter((criterion) => !claimed.has(criterion.criterionStableKey));
    if (missing.length > 0)
        return changed(`TMS has no slider for ${missing.map((entry) => `"${entry.name}"`).join(", ")}`);
    return { ok: true, planned };
}
/**
 * The body a browser would send for this form with "Save Draft" pressed, with only OUR values
 * put in: every slider our mark, Notes our text, every match box ticked (ruling CC-Q15: as if
 * `Mark All`), and every hidden field - the CSRF token, the person, the role, the mark id -
 * echoed exactly as TMS rendered it.
 */
function planPost(read, plan) {
    const matched = matchCriteria(read.form, plan);
    if ("kind" in matched)
        return matched;
    const bySlider = new Map(matched.planned.map((entry) => [`criteria[${entry.tmsCriterionId}]`, entry]));
    const notesSent = crlf(plan.text);
    const entries = [];
    for (const control of read.controls) {
        if (control.inDatalist || control.name === "")
            continue;
        const value = attributeOf(control, "value")?.value;
        if (isSubmit(control)) {
            if (control.name === "status" && value === TMS_MARK_DRAFT_STATUS)
                entries.push({ name: "status", value: TMS_MARK_DRAFT_STATUS });
            continue; // Save Final is never pressed, and so never sent
        }
        if (control.tag === "button" || control.disabled)
            continue;
        if (control.tag === "input" && (control.type === "reset" || control.type === "button"))
            continue;
        switch (control.name) {
            case "_token":
            case "person_id":
            case "grading_role_id":
            case "mark_id":
                entries.push({ name: control.name, value: value ?? "" });
                break;
            case "all":
                entries.push({ name: "all", value: value ?? "on" });
                break;
            case "matches[]":
                entries.push({ name: "matches[]", value: value ?? "on" });
                break;
            case "notes":
                entries.push({ name: "notes", value: notesSent });
                break;
            default: {
                const slider = bySlider.get(control.name);
                if (slider === undefined)
                    return refused("form_changed", `The TMS marking form carries "${fragment(control.name)}", which this build did not plan.`);
                entries.push({ name: control.name, value: String(slider.sent) });
            }
        }
    }
    assertDraftOnly(entries);
    return {
        kind: "post",
        body: encodeFormUrlEncoded(entries),
        criteria: matched.planned,
        matches: read.form.matches.map((match) => ({ ...match, sent: !match.disabled })),
        notesSent,
    };
}
async function load(prepared, url, redactor) {
    guardTmsMarkingRequest("GET", url, prepared.origin, prepared.originOptions);
    let response;
    try {
        response = await prepared.transport.getMarkingPage(url);
    }
    catch (error) {
        return { kind: "lost", error: redactor.fragment(errorMessage(error)) };
    }
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null)
        return { kind: "violation", violation: malformed };
    if (prepared.redirects === "manual" && response.redirected) {
        return { kind: "violation", violation: "it answered with a redirect it followed, although it promised never to follow one" };
    }
    return { kind: "answer", response: response };
}
function loadRefusal(loaded, what) {
    if (loaded.kind === "lost")
        return refused("unreachable", `TMS could not be reached to read ${what} (${loaded.error}).`);
    if (loaded.kind === "violation")
        return refused("transport_unavailable", `The TMS Push transport broke its own rules reading ${what} (${loaded.violation}).`);
    return null;
}
/** "final" as a word in TMS's words for a mark. */
function saysFinal(text) {
    return text !== null && /\bfinal\b/i.test(text);
}
/**
 * Read both competitions' marking indexes, find the official, decide create or update, and read
 * that form. Sends nothing.
 */
async function locate(prepared, redactor) {
    const { plan } = prepared;
    const person = plan.subject.tmsPersonId;
    const who = `${plan.subject.mediaName} (TMS person ${person})`;
    const found = [];
    const indexMessages = new Map();
    const notes = [];
    for (const competitionId of TMS_MARKING_COMPETITION_IDS) {
        const url = tmsMarkingIndexUrl(prepared.origin, competitionId);
        const loaded = await load(prepared, url, redactor);
        const lost = loadRefusal(loaded, `the marking index of competition ${competitionId}`);
        if (lost !== null)
            return lost;
        const index = readMarkingIndexResponse(loaded.response, competitionId, prepared.origin, prepared.originOptions);
        if (index.kind === "refused")
            return index;
        indexMessages.set(competitionId, index.messages);
        const rows = index.entries.filter((entry) => entry.tmsPersonId === person);
        if (rows.length > 1) {
            return refused("form_changed", `The marking index of competition ${competitionId} lists ${who} ${String(rows.length)} times, so which record is theirs is not certain.`, TMS_MARKING_FALLBACK);
        }
        const row = rows[0];
        if (row === undefined)
            continue;
        if (row.problem !== null)
            return refused("form_changed", `The marking index row for ${who} in competition ${competitionId} cannot be read for certain: ${row.problem}.`, TMS_MARKING_FALLBACK);
        if (!tmsNameMatches(row.rowText, plan.subject.mediaName)) {
            return refused("form_changed", `The marking index of competition ${competitionId} names TMS person ${person} as "${fragment(row.rowText)}", which does not name ${plan.subject.mediaName}. A push to it could mark someone else.`, TMS_MARKING_FALLBACK);
        }
        if (row.unknownMarkLinks.length > 0) {
            return refused("form_changed", `The marking index row for ${who} in competition ${competitionId} links to an existing mark in a way this build does not recognise (${quoted(row.unknownMarkLinks)}). Creating a new mark could duplicate it.`, TMS_MARKING_FALLBACK);
        }
        // "No Marks" is TMS's own way of printing an EMPTY Mark column — the real index says it
        // in words where the build's fixtures left the cell blank, and the first live push
        // (27 Sep 2026, SINGH Jaswant) was refused for exactly that phrase. It is the one wording
        // that means the opposite of "something is here this build cannot read": it is TMS saying
        // there is nothing here at all, so a create is the SAFE branch. Matched exactly, ignoring
        // case and surrounding space — "No Marks (2)" or any other wording still refuses below.
        const marklessInWords = row.markText !== null && /^no marks$/i.test(row.markText.trim());
        if (row.marks.length === 0 && !marklessInWords && row.markText !== null && /[\p{L}\p{N}]/u.test(row.markText)) {
            return refused("form_changed", `The marking index shows "${fragment(row.markText)}" in the Mark column for ${who} in competition ${competitionId}, but no edit link this build recognises. Creating a new mark could duplicate it.`, TMS_MARKING_FALLBACK);
        }
        if (index.strayMarkLinks.length > 0 && row.marks.length === 0) {
            notes.push(`The marking index of competition ${competitionId} shows mark links no official's row claims (${quoted(index.strayMarkLinks)}).`);
        }
        found.push(row);
    }
    if (found.length === 0) {
        return refused("rejected_by_tms", `${who} is not listed in the TMS marking index of competition ${TMS_MARKING_COMPETITION_IDS.join(" or ")}, so TMS offers no marking form for them.`, `Check the official's appointment in TMS. ${TMS_MARKING_FALLBACK}`);
    }
    const roles = new Set(found.map((row) => row.gradingRoleId));
    if (roles.size > 1) {
        return refused("form_changed", `TMS lists ${who} under different grading roles in the two competitions (${[...roles].join(", ")}), so which marking is theirs is not certain.`, TMS_MARKING_FALLBACK);
    }
    const marks = new Map();
    for (const row of found)
        for (const mark of row.marks)
            if (!marks.has(mark.markId))
                marks.set(mark.markId, { row, url: mark.url });
    if (marks.size > 1) {
        return refused("form_changed", `TMS already holds ${String(marks.size)} marks for ${who} (marks ${[...marks.keys()].join(", ")}). One record per official is the rule, so this build will not choose between them.`, `Resolve it in TMS by hand. ${TMS_MARKING_FALLBACK}`);
    }
    const existing = [...marks.entries()][0];
    let target;
    if (existing !== undefined) {
        const [markId, { row, url }] = existing;
        if (saysFinal(row.markText ?? row.rowText)) {
            return refused("rejected_by_tms", `TMS shows the mark for ${who} as Final ("${fragment(row.markText ?? "")}"). This build saves drafts only and never changes a Final mark.`, "Reopen it inside TMS if it must change, and enter the change there by hand.");
        }
        target = { competitionId: row.competitionId, mode: "update", markId, formUrl: url, gradingRoleId: row.gradingRoleId, rowText: row.rowText, markText: row.markText };
    }
    else {
        const row = found[0];
        target = { competitionId: row.competitionId, mode: "create", markId: null, formUrl: row.createUrl, gradingRoleId: row.gradingRoleId, rowText: row.rowText, markText: row.markText };
    }
    const what = target.mode === "create" ? `the new-mark form for ${who}` : `the form of mark ${target.markId ?? ""} for ${who}`;
    const loaded = await load(prepared, target.formUrl, redactor);
    const lost = loadRefusal(loaded, what);
    if (lost !== null)
        return lost;
    const form = readMarkFormResponse(loaded.response, { competitionId: target.competitionId, tmsPersonId: person, gradingRoleId: target.gradingRoleId, markId: target.markId ?? "", base: prepared.origin, formUrl: target.formUrl }, prepared.originOptions);
    if (form.kind === "refused")
        return form;
    redactor.addToken(form.read.token);
    return { kind: "located", target, read: form.read, indexMessages: indexMessages.get(target.competitionId) ?? { errors: [], scriptErrors: [], guardText: null, saved: null }, notes };
}
/* -- 5. Checking the plan's own shape (a programmer error throws, before any request) --------- */
const STABLE_KEY = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Throws, naming the problem, for a plan no push should ever be asked to send. */
export function validateReportPushPlan(plan, where = "validateReportPushPlan") {
    const fail = (why) => {
        throw new Error(`${where}: ${why}`);
    };
    if (plan === null || typeof plan !== "object")
        fail("there is no plan.");
    const value = plan;
    if (value["kind"] !== "report")
        fail("the plan is not a report push.");
    if (!isTmsPushPressId(value["pressId"]))
        fail("the plan names no press.");
    const subject = value["subject"];
    if (subject === null || typeof subject !== "object")
        fail("the plan names no official.");
    const personId = subject["tmsPersonId"];
    if (typeof personId !== "string" || !/^[1-9][0-9]{0,11}$/.test(personId))
        fail("the official's TMS person id is not digits.");
    const name = subject["mediaName"];
    if (typeof name !== "string" || !/\p{L}/u.test(name) || name.length > 200)
        fail("the official has no name to check against TMS's own.");
    if (!TMS_MARKING_SUBJECT_ROLES.includes(subject["role"]))
        fail("the official's role is not one the TD reports on.");
    const criteria = value["criteria"];
    if (!Array.isArray(criteria) || criteria.length === 0 || criteria.length > TMS_MARKING_LIMITS.criteria)
        fail("the plan carries no criteria set.");
    const keys = new Set();
    for (const [index, raw] of criteria.entries()) {
        const criterion = raw;
        if (criterion === null || typeof criterion !== "object")
            fail(`criterion ${String(index + 1)} is not a criterion.`);
        const key = criterion["criterionStableKey"];
        if (typeof key !== "string" || !STABLE_KEY.test(key) || keys.has(key))
            fail(`criterion ${String(index + 1)} has no unique stable key.`);
        keys.add(key);
        const criterionName = criterion["name"];
        if (typeof criterionName !== "string" || criterionWords(criterionName) === "")
            fail(`criterion ${String(index + 1)} has no name.`);
        const weight = criterion["weight"];
        if (typeof weight !== "number" || !Number.isInteger(weight) || weight < 0 || weight > 200)
            fail(`criterion ${String(index + 1)} has no weight.`);
        const mark = criterion["value"];
        if (typeof mark !== "number" || !Number.isInteger(mark) || mark < 0 || mark > 100 || mark % 10 !== 0) {
            fail(`criterion ${String(index + 1)} carries no mark from 0 to 100 in tens - a push never sends a slider nobody set.`);
        }
    }
    const text = value["text"];
    if (typeof text !== "string" || text.trim() === "" || text.length > TMS_MARKING_LIMITS.notes)
        fail("the plan carries no report text.");
}
function prepareRequest(transport, plan, options, where) {
    if (transport === null || typeof transport !== "object")
        throw new Error(`${where}: there is no transport.`);
    if (typeof transport.name !== "string" || transport.name.trim() === "" || transport.name.length > 100)
        throw new Error(`${where}: the transport has no name.`);
    if (transport.redirects !== "manual" && transport.redirects !== "follow")
        throw new Error(`${where}: the transport must say how it handles redirects.`);
    if (typeof transport.getMarkingPage !== "function" || typeof transport.postMark !== "function")
        throw new Error(`${where}: the transport cannot read or post a mark form.`);
    validateReportPushPlan(plan, where);
    const originOptions = options.testOnlyOrigin === true ? { testOnlyOrigin: true } : {};
    const origin = requirePinnedOrigin(transport.base, originOptions, where);
    const clock = options.now ?? (() => new Date());
    let startAt;
    try {
        const at = clock();
        if (!(at instanceof Date) || !Number.isFinite(at.getTime()))
            throw new Error("no valid time");
        startAt = at.toISOString();
    }
    catch (error) {
        throw new Error(`${where}: the clock failed (${fragment(errorMessage(error))}).`);
    }
    return { transport, name: transport.name, redirects: transport.redirects, origin, originOptions, plan, clock, startAt };
}
function safeInstant(prepared) {
    try {
        const at = prepared.clock();
        return Number.isFinite(at.getTime()) ? at.toISOString() : prepared.startAt;
    }
    catch {
        return prepared.startAt;
    }
}
function clean(redactor, text, limit) {
    return redactor.final(text, limit);
}
/**
 * READ-ONLY. Both marking indexes, the official's form - and NO post. What the confirm window
 * shows: where the report goes (a new mark, or the one TMS holds), every slider as TMS captions it
 * with its value now and the mark it will carry, the matches it will be tied to, and the Notes
 * text now and as it will be saved - or, instead, why it cannot go.
 */
export async function previewReportPush(transport, plan, options = {}) {
    const prepared = prepareRequest(transport, plan, options, "previewReportPush");
    const redactor = new Redactor();
    const empty = (reason, detail, target = null, warnings = []) => ({
        transport: prepared.name,
        at: safeInstant(prepared),
        posts: 0,
        ready: false,
        refusal: { reason, detail: clean(redactor, detail, TMS_MARKING_LIMITS.detail) },
        target,
        criteria: [],
        matches: [],
        notesNow: { chars: 0, text: "" },
        notesSent: { chars: plan.text.length, text: plan.text },
        warnings: warnings.map((line) => clean(redactor, line, TMS_MARKING_LIMITS.collateral)),
    });
    try {
        const located = await locate(prepared, redactor);
        if (located.kind === "refused")
            return empty(located.reason, located.detail);
        const planned = planPost(located.read, plan);
        if (planned.kind === "refused")
            return empty(planned.reason, planned.detail, located.target, located.notes);
        const now = located.read.form.notes;
        return {
            transport: prepared.name,
            at: safeInstant(prepared),
            posts: 0,
            ready: true,
            refusal: null,
            target: {
                ...located.target,
                rowText: clean(redactor, located.target.rowText, 400),
                markText: located.target.markText === null ? null : clean(redactor, located.target.markText, 200),
            },
            criteria: planned.criteria.map((entry) => ({ ...entry, caption: clean(redactor, entry.caption, 200), now: entry.now === null ? null : clean(redactor, entry.now, 20) })),
            matches: planned.matches.map((entry) => ({ ...entry, label: clean(redactor, entry.label, 200) })),
            notesNow: { chars: now.length, text: redactor.apply(now.slice(0, 2000)) },
            notesSent: { chars: plan.text.length, text: plan.text },
            warnings: located.notes.map((line) => clean(redactor, line, TMS_MARKING_LIMITS.collateral)),
        };
    }
    catch (error) {
        return empty("transport_unavailable", `The preview failed while reading TMS (${redactor.fragment(errorMessage(error))}). Nothing was sent. Press Push again.`);
    }
}
/** TMS's answer to the save, as far as its status and words go - the read-back decides the rest. */
export function classifyMarkPostResponse(response, baseline) {
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null)
        return { kind: "unknown", detail: `TMS's answer to the save could not be read (${malformed}).` };
    const status = response.status;
    if (status === 0 || (status >= 300 && status < 400)) {
        return { kind: "unknown", detail: "TMS answered the save with a redirect that was not followed, so only the read-back can tell what it saved." };
    }
    if (status === 419) {
        return {
            kind: "failed",
            reason: "session_expired",
            definite: true,
            detail: "TMS answered the save with 419 Page Expired: the form's security token was stale or the TMS session had ended, so TMS refused it before reading it. It was not retried.",
        };
    }
    const page = readHtmlPage(response.body);
    if (status === 401 || page.passwordInputs > 0 || /(^|\/)(login|signin|sign-in)(\/|$)/i.test(linkPath(response.url))) {
        return { kind: "failed", reason: "session_expired", definite: true, detail: "TMS answered the save with its sign-in page, so the TMS session had ended and the save was refused. It was not retried." };
    }
    if (status >= 500)
        return { kind: "failed", reason: "rejected_by_tms", definite: false, detail: `TMS answered the save with HTTP ${String(status)}, a server error, so its answer does not say whether anything was saved.` };
    if (status < 200 || status >= 300)
        return { kind: "failed", reason: "rejected_by_tms", definite: status >= 400, detail: `TMS refused the save with HTTP ${String(status)}.` };
    const messages = readTmsPageMessages(response.body);
    const fresh = newAlerts(messages, baseline);
    if (fresh.length > 0)
        return { kind: "failed", reason: "rejected_by_tms", definite: true, detail: `TMS answered the save with an error: ${quoted(fresh)}.` };
    return { kind: "unknown", detail: `TMS answered the save with HTTP ${String(status)} and no redirect, so only the read-back can tell what it saved.` };
}
function newAlerts(page, baseline) {
    const known = new Set([...baseline.errors, ...baseline.scriptErrors]);
    return [...new Set([...page.errors, ...page.scriptErrors])].filter((text) => !known.has(text));
}
/** Notes compared as TMS keeps them: line breaks as one, and trimmed (Laravel's TrimStrings). */
function sameNotes(a, b) {
    const norm = (text) => text.replace(/\r\n|\r/g, "\n").trim();
    return norm(a) === norm(b);
}
function readBackOf(form, planned) {
    return {
        markId: form.markId,
        criteria: planned.map((entry) => {
            const raw = form.criteria.find((slider) => slider.tmsCriterionId === entry.tmsCriterionId)?.value ?? null;
            const value = raw !== null && /^[0-9]{1,3}$/.test(raw.trim()) ? Number(raw.trim()) : null;
            return { criterionStableKey: entry.criterionStableKey, tmsCriterionId: entry.tmsCriterionId, value };
        }),
        notes: form.notes,
        matches: form.matches.filter((match) => match.checked).map((match) => match.value),
    };
}
function matchesReadBack(post, readBack, markAllTicked) {
    const sentMatches = post.matches.filter((match) => match.sent).map((match) => match.value).sort();
    const shownMatches = [...readBack.matches].sort();
    if (sentMatches.join(",") === shownMatches.join(","))
        return "as_sent";
    return markAllTicked ? "mark_all" : "differs";
}
/** Every difference between what was sent and what the verifying read shows, in words. */
function differences(post, readBack, markAllTicked) {
    const out = [];
    for (const entry of post.criteria) {
        const shown = readBack.criteria.find((item) => item.tmsCriterionId === entry.tmsCriterionId)?.value ?? null;
        if (shown !== entry.sent)
            out.push(`"${entry.name}" reads back ${shown === null ? "with no value" : String(shown)}, not ${String(entry.sent)}`);
    }
    if (!sameNotes(readBack.notes, post.notesSent))
        out.push(`the Notes read back with ${String(readBack.notes.length)} characters that are not the report sent`);
    if (matchesReadBack(post, readBack, markAllTicked) === "differs") {
        const sent = post.matches.filter((match) => match.sent).length;
        out.push(`${String(readBack.matches.length)} matches read back ticked, not the ${String(sent)} sent, and the Mark All box is not ticked either`);
    }
    return out;
}
/**
 * Push one official's report - and the marks it travels with - to TMS as a DRAFT: read both
 * indexes and the form fresh, plan, the caller's last word, POST ONCE, then read the index (for
 * TMS's words, and the mark a create made) and the mark's own form (for its values) and verify.
 *
 * Resolves for every outcome once its arguments are sound; throws only for a call that should
 * never have been made, before any request.
 */
export async function pushReportDraft(transport, plan, options = {}) {
    const prepared = prepareRequest(transport, plan, options, "pushReportDraft");
    const redactor = new Redactor();
    const collateral = [];
    let posts = 0;
    let target = null;
    const finish = (outcome, reason, detail, readBack) => {
        const lines = [];
        for (const line of collateral) {
            const text = clean(redactor, line, TMS_MARKING_LIMITS.collateral);
            if (text !== "" && !lines.includes(text))
                lines.push(text);
        }
        return {
            transport: prepared.name,
            at: safeInstant(prepared),
            posts,
            outcome,
            reason: outcome === "accepted_draft" ? null : reason,
            detail: clean(redactor, detail, TMS_MARKING_LIMITS.detail) || "No detail was recorded. Check the official's marking in TMS by eye.",
            target,
            readBack: readBack === null ? null : { ...readBack, notes: redactor.apply(readBack.notes).slice(0, TMS_MARKING_LIMITS.notes) },
            collateral: lines.slice(0, TMS_MARKING_LIMITS.collateralCount),
        };
    };
    try {
        /* Step 1: find the official and read their form - fresh, with a fresh token. */
        const located = await locate(prepared, redactor);
        if (located.kind === "refused")
            return finish("failed", located.reason, located.detail, null);
        collateral.push(...located.notes);
        target = { competitionId: located.target.competitionId, mode: located.target.mode, markId: located.target.markId };
        const post = planPost(located.read, plan);
        if (post.kind === "refused")
            return finish("failed", post.reason, post.detail, null);
        redactor.addSecret(post.body);
        /* Step 2: the caller's last word - the extension's hold. */
        if (options.beforePost !== undefined) {
            let stop = null;
            try {
                const answer = options.beforePost();
                const ok = answer !== null && typeof answer === "object" && answer.ok === true;
                if (!ok) {
                    const words = answer !== null && typeof answer === "object" ? answer.detail : undefined;
                    stop = typeof words === "string" && words.trim() !== "" ? words : "the push was stopped just before it was sent.";
                }
            }
            catch (error) {
                stop = `the push was stopped just before it was sent (${redactor.fragment(errorMessage(error))}).`;
            }
            if (stop !== null)
                return finish("failed", "transport_unavailable", `Nothing was sent to TMS: ${stop}`, null);
        }
        /* Step 3: the post, exactly once, as a draft. Never repeated. */
        const saveUrl = guardTmsMarkingRequest("POST", tmsMarkSaveUrl(prepared.origin, located.target.competitionId), prepared.origin, prepared.originOptions).url;
        posts = 1;
        let answer;
        try {
            const response = await transport.postMark(located.target.competitionId, post.body, saveUrl);
            const malformed = tmsHttpResponseProblem(response);
            if (malformed !== null)
                answer = { kind: "unknown", detail: `TMS's answer to the save cannot be believed (${malformed}).` };
            else if (prepared.redirects === "manual" && response.redirected) {
                collateral.push("The TMS Push transport followed a redirect after the save, although it promised never to: check the official's marking in TMS by eye, and report this.");
                answer = { kind: "unknown", detail: "TMS's answer to the save came through a redirect the transport should not have followed." };
            }
            else
                answer = classifyMarkPostResponse(response, located.read.form.messages);
        }
        catch (error) {
            answer = { kind: "lost", detail: `The connection failed before TMS's answer to the save arrived (${redactor.fragment(errorMessage(error))}).` };
        }
        /* Step 4: the index for TMS's words (this load takes any flash) and the mark's id ... */
        const who = `${plan.subject.mediaName} (TMS person ${plan.subject.tmsPersonId})`;
        const indexUrl = tmsMarkingIndexUrl(prepared.origin, located.target.competitionId);
        const afterIndex = await load(prepared, indexUrl, redactor);
        let unverifiable = null;
        let markUrl = null;
        let markId = located.target.markId;
        const refusalWords = [];
        let finalShown = false;
        let draftShown = false;
        if (afterIndex.kind !== "answer") {
            unverifiable = afterIndex.kind === "lost" ? `TMS could not be reached to read the marking index back (${afterIndex.error}).` : `the TMS Push transport broke its own rules (${afterIndex.violation}).`;
        }
        else {
            const index = readMarkingIndexResponse(afterIndex.response, located.target.competitionId, prepared.origin, prepared.originOptions);
            if (index.kind === "refused")
                unverifiable = index.detail;
            else {
                refusalWords.push(...newAlerts(index.messages, located.indexMessages));
                const row = index.entries.find((entry) => entry.tmsPersonId === plan.subject.tmsPersonId);
                if (row === undefined)
                    unverifiable = `the marking index no longer lists ${who}.`;
                else {
                    finalShown = saysFinal(row.markText ?? row.rowText);
                    draftShown = /\bdraft\b/i.test(row.markText ?? row.rowText);
                    if (located.target.mode === "update") {
                        const same = row.marks.find((mark) => mark.markId === located.target.markId);
                        if (same === undefined)
                            unverifiable = `the marking index no longer links mark ${located.target.markId ?? ""} for ${who}.`;
                        else
                            markUrl = same.url;
                        if (row.marks.length > 1)
                            collateral.push(`TMS now shows ${String(row.marks.length)} marks for ${who} in competition ${located.target.competitionId}: check them in TMS by eye.`);
                    }
                    else if (row.marks.length === 1) {
                        markUrl = row.marks[0]?.url ?? null;
                        markId = row.marks[0]?.markId ?? null;
                        target = { competitionId: located.target.competitionId, mode: "create", markId };
                    }
                    else if (row.marks.length === 0) {
                        unverifiable = `TMS shows no mark for ${who} after the save.`;
                    }
                    else {
                        collateral.push(`TMS now shows ${String(row.marks.length)} marks for ${who} in competition ${located.target.competitionId} where there were none: check them in TMS by eye and remove any duplicate there.`);
                        unverifiable = `TMS shows ${String(row.marks.length)} marks for ${who} after the save, so which one it saved is not certain.`;
                    }
                }
            }
        }
        /* ... then the mark's own form for its values: a LATER load than the flash. */
        let readBack = null;
        let markAllTicked = false;
        if (unverifiable === null && markUrl !== null && markId !== null) {
            const loaded = await load(prepared, markUrl, redactor);
            if (loaded.kind !== "answer") {
                unverifiable = loaded.kind === "lost" ? `TMS could not be reached to read the mark back (${loaded.error}).` : `the TMS Push transport broke its own rules (${loaded.violation}).`;
            }
            else {
                const form = readMarkFormResponse(loaded.response, { competitionId: located.target.competitionId, tmsPersonId: plan.subject.tmsPersonId, gradingRoleId: located.target.gradingRoleId, markId, base: prepared.origin, formUrl: markUrl }, prepared.originOptions);
                if (form.kind === "refused")
                    unverifiable = form.detail;
                else {
                    redactor.addToken(form.read.token);
                    const fresh = newAlerts(form.read.form.messages, located.read.form.messages);
                    if (fresh.length > 0)
                        refusalWords.push(...fresh);
                    readBack = readBackOf(form.read.form, post.criteria);
                    markAllTicked = form.read.form.all.present && form.read.form.all.checked;
                }
            }
        }
        /* Step 5: decide. The read-back is the receipt; TMS's toast is only words. */
        const answerWords = answer.detail;
        if (answer.kind === "failed" && answer.definite)
            return finish("failed", answer.reason, `${answerWords} ${adviceFor(answer.reason)}`, readBack);
        if (refusalWords.length > 0) {
            return finish("failed", "rejected_by_tms", `TMS refused the save: ${quoted([...new Set(refusalWords)])}. Correct what TMS objected to, then press Push again - or ${TMS_MARKING_FALLBACK.charAt(0).toLowerCase()}${TMS_MARKING_FALLBACK.slice(1)}`, readBack);
        }
        if (readBack === null) {
            return finish("failed", answer.kind === "failed" ? answer.reason : "unreachable", `The save of ${who}'s report may or may not have landed in TMS. ${answerWords} It could not be read back: ${unverifiable ?? "no mark to read."} Check the official's marking in TMS by eye before pressing again.`, null);
        }
        const wrong = differences(post, readBack, markAllTicked);
        if (wrong.length > 0) {
            return finish("failed", answer.kind === "failed" ? answer.reason : "rejected_by_tms", `TMS did not save the report as sent: ${wrong.join("; ")}. ${answerWords} Check the official's marking in TMS by eye before pressing again.`, readBack);
        }
        if (finalShown) {
            return finish("failed", "rejected_by_tms", `The report reads back as sent, but TMS's marking index shows this mark as Final - this build sent it as a draft. Check it in TMS by eye.`, readBack);
        }
        const where = located.target.mode === "create" ? `as a new draft mark (mark ${markId ?? ""}) in competition ${located.target.competitionId}` : `into mark ${markId ?? ""} in competition ${located.target.competitionId}, updating it`;
        const matchesSent = post.matches.filter((match) => match.sent).length;
        const checked = matchesReadBack(post, readBack, markAllTicked) === "mark_all"
            ? `${String(post.criteria.length)} marks and ${String(plan.text.length)} characters of report text as sent. TMS holds the mark as Mark All - the whole tournament - rather than the ${String(matchesSent)} matches ticked one by one (${String(readBack.matches.length)} read back ticked individually); the Technical Delegate ruled on 30 Sep 2026 that Mark All is an acceptable save.`
            : `${String(post.criteria.length)} marks, ${String(matchesSent)} matches and ${String(plan.text.length)} characters of report text as sent.`;
        return finish("accepted_draft", null, `Saved in TMS ${where} with Save Draft, and checked by reading it back: ${checked}` +
            (draftShown ? " TMS's marking index shows it as a draft." : " Publishing or finalising stays a deliberate step inside TMS."), readBack);
    }
    catch (error) {
        const why = redactor.fragment(errorMessage(error));
        if (posts === 0)
            return finish("failed", "transport_unavailable", `Nothing was sent to TMS: the push failed before the save (${why}). ${adviceFor("transport_unavailable")}`, null);
        return finish("failed", "unreachable", `The save may or may not have landed in TMS: the push failed after sending it (${why}). Check the official's marking in TMS by eye before pressing again.`, null);
    }
}
/* -- 8. The result message: one reading, shared by the extension, the page and the API ------- */
export const TMS_REPORT_PUSH_RESULT_TYPE = "hockeytd.tms-push.report-result";
export const TMS_REPORT_PUSH_RESULT_VERSION = 1;
export const TMS_REPORT_PUSH_RESULT_LIMITS = {
    detail: 2000,
    at: 40,
    collateral: 100,
    collateralItem: 300,
    notes: TMS_MARKING_LIMITS.notes,
    criteria: TMS_MARKING_LIMITS.criteria,
    matches: TMS_MARKING_LIMITS.matches,
};
class ResultRefusal extends Error {
}
function refuseResult(problem) {
    throw new ResultRefusal(problem);
}
function exactKeys(value, where, keys) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        refuseResult(`${where} is not an object`);
    for (const key of Object.keys(value))
        if (!keys.includes(key))
            refuseResult(`${where} has a field the API does not know: "${key.slice(0, 40)}"`);
    for (const key of keys)
        if (!Object.prototype.hasOwnProperty.call(value, key))
            refuseResult(`${where} is missing ${key}`);
    return value;
}
function boundedText(value, where, max, nonBlank) {
    if (typeof value !== "string" || value.length > max || (nonBlank && value.trim() === ""))
        refuseResult(`${where} is not ${nonBlank ? "a non-blank" : "a"} string of at most ${String(max)} characters`);
    return value;
}
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/;
const DIGITS = /^[1-9][0-9]{0,15}$/;
/**
 * Read one report-push result for press `pressId` - by the extension (every result its confirm
 * page hands the background), the Control Centre page (before it reports) and the API (the one
 * that decides). One function, so the three can never disagree about what a result is.
 */
export function readTmsReportPushResultMessage(raw, pressId) {
    try {
        const value = exactKeys(raw, "the result", [
            "type",
            "version",
            "pressId",
            "cancelled",
            "outcome",
            "reason",
            "detail",
            "at",
            "target",
            "readBack",
            "collateral",
            "postsMade",
            "transport",
            "extensionVersion",
        ]);
        if (value["type"] !== TMS_REPORT_PUSH_RESULT_TYPE)
            refuseResult(`the result's type is not "${TMS_REPORT_PUSH_RESULT_TYPE}"`);
        if (value["version"] !== TMS_REPORT_PUSH_RESULT_VERSION)
            refuseResult(`the result's version is not ${String(TMS_REPORT_PUSH_RESULT_VERSION)}`);
        const press = value["pressId"];
        if (!isTmsPushPressId(press))
            refuseResult("the result names no press");
        if (press.toLowerCase() !== String(pressId).toLowerCase())
            refuseResult("the result is for another press");
        if (typeof value["cancelled"] !== "boolean")
            refuseResult("the result does not say whether it was cancelled");
        const postsMade = value["postsMade"];
        if (postsMade !== 0 && postsMade !== 1)
            refuseResult("the result counts other than 0 or 1 posts");
        const outcome = value["outcome"];
        if (outcome !== "accepted_draft" && outcome !== "failed")
            refuseResult('the result\'s outcome is not "accepted_draft" or "failed"');
        const reason = value["reason"];
        if (outcome === "failed" && !TMS_PUSH_FAILURE_WORDS.includes(reason))
            refuseResult(`the result's reason is not one of ${TMS_PUSH_FAILURE_WORDS.join(", ")}`);
        if (outcome === "accepted_draft" && reason !== null)
            refuseResult("the result gives a reason on an acceptance");
        boundedText(value["detail"], "the result's detail", TMS_REPORT_PUSH_RESULT_LIMITS.detail, true);
        const at = value["at"];
        if (typeof at !== "string" || at.length > TMS_REPORT_PUSH_RESULT_LIMITS.at || !ISO_INSTANT.test(at) || !Number.isFinite(Date.parse(at)))
            refuseResult("the result's at is not an ISO 8601 instant with its zone");
        const target = value["target"];
        if (target !== null) {
            const fields = exactKeys(target, "the result's target", ["competitionId", "mode", "markId"]);
            if (!TMS_MARKING_COMPETITION_IDS.includes(fields["competitionId"]))
                refuseResult('the result\'s target competition is not "1916" or "1917"');
            if (fields["mode"] !== "create" && fields["mode"] !== "update")
                refuseResult('the result\'s target mode is not "create" or "update"');
            const markId = fields["markId"];
            if (markId !== null && (typeof markId !== "string" || !DIGITS.test(markId)))
                refuseResult("the result's target mark is not a TMS mark id");
            if (fields["mode"] === "update" && markId === null)
                refuseResult("the result updates a mark it does not name");
        }
        const readBack = value["readBack"];
        if (readBack !== null) {
            const fields = exactKeys(readBack, "the result's readBack", ["markId", "criteria", "notes", "matches"]);
            if (typeof fields["markId"] !== "string" || !DIGITS.test(fields["markId"]))
                refuseResult("the read-back names no TMS mark");
            const criteria = fields["criteria"];
            if (!Array.isArray(criteria) || criteria.length === 0 || criteria.length > TMS_REPORT_PUSH_RESULT_LIMITS.criteria)
                refuseResult("the read-back carries no criteria");
            const keys = new Set();
            criteria.forEach((entry, index) => {
                const item = exactKeys(entry, `the read-back's criteria[${String(index)}]`, ["criterionStableKey", "tmsCriterionId", "value"]);
                const key = item["criterionStableKey"];
                if (typeof key !== "string" || !STABLE_KEY.test(key) || key.length > 80 || keys.has(key))
                    refuseResult(`the read-back's criteria[${String(index)}] has no unique stable key`);
                keys.add(key);
                if (typeof item["tmsCriterionId"] !== "string" || !DIGITS.test(item["tmsCriterionId"]))
                    refuseResult(`the read-back's criteria[${String(index)}] names no TMS criterion`);
                const mark = item["value"];
                if (mark !== null && (typeof mark !== "number" || !Number.isInteger(mark) || mark < 0 || mark > 100))
                    refuseResult(`the read-back's criteria[${String(index)}] value is not 0 to 100`);
            });
            boundedText(fields["notes"], "the read-back's notes", TMS_REPORT_PUSH_RESULT_LIMITS.notes, false);
            const matches = fields["matches"];
            if (!Array.isArray(matches) || matches.length > TMS_REPORT_PUSH_RESULT_LIMITS.matches || !matches.every((id) => typeof id === "string" && DIGITS.test(id))) {
                refuseResult("the read-back's matches are not TMS match ids");
            }
        }
        const collateral = value["collateral"];
        if (!Array.isArray(collateral) || collateral.length > TMS_REPORT_PUSH_RESULT_LIMITS.collateral)
            refuseResult("the collateral warnings are not a short list");
        collateral.forEach((item, index) => boundedText(item, `collateral[${String(index)}]`, TMS_REPORT_PUSH_RESULT_LIMITS.collateralItem, false));
        const version = value["extensionVersion"];
        if (!isTmsPushExtensionVersion(version))
            refuseResult("the result's extensionVersion is not digits and dots");
        if (value["transport"] !== `chrome-extension/${version}`)
            refuseResult(`the result's transport is not "chrome-extension/${version}"`);
        if (value["cancelled"] === true && (postsMade !== 0 || outcome !== "failed"))
            refuseResult("the result is cancelled and counts a post or an acceptance");
        if (outcome === "accepted_draft" && (postsMade !== 1 || readBack === null || target === null))
            refuseResult("the result accepts a save it did not make or did not read back");
        if (postsMade === 0 && readBack !== null)
            refuseResult("the result reads back a save it did not make");
        return { ok: true, message: raw };
    }
    catch (error) {
        if (error instanceof ResultRefusal)
            return { ok: false, problem: error.message };
        return { ok: false, problem: "the result could not be read" };
    }
}
/** The core's result, as the one message the extension sends and the API reads. */
export function reportPushResultMessage(input) {
    const { core } = input;
    return {
        type: TMS_REPORT_PUSH_RESULT_TYPE,
        version: TMS_REPORT_PUSH_RESULT_VERSION,
        pressId: input.pressId,
        cancelled: input.cancelled,
        outcome: core.outcome,
        reason: core.outcome === "accepted_draft" ? null : (core.reason ?? "transport_unavailable"),
        detail: core.detail,
        at: core.at,
        target: core.target,
        readBack: core.readBack,
        collateral: core.collateral,
        postsMade: core.posts,
        transport: `chrome-extension/${input.extensionVersion}`,
        extensionVersion: input.extensionVersion,
    };
}
/**
 * Whether a read-back shows exactly the plan: every criterion of the set at its planned value,
 * and the Notes as the planned text. What the API believes, and all it believes, before it
 * records a report push as accepted - never the extension's own word for it.
 */
export function reportReadBackMatchesPlan(readBack, planned) {
    if (readBack === null)
        return { matches: false, differences: ["nothing was read back"] };
    const out = [];
    for (const criterion of planned.criteria) {
        const shown = readBack.criteria.filter((entry) => entry.criterionStableKey === criterion.criterionStableKey);
        if (shown.length !== 1)
            out.push(`${criterion.criterionStableKey} is read back ${String(shown.length)} times`);
        else if (shown[0]?.value !== criterion.value)
            out.push(`${criterion.criterionStableKey} reads back ${String(shown[0]?.value ?? "empty")}, not ${String(criterion.value)}`);
    }
    if (readBack.criteria.length !== planned.criteria.length)
        out.push(`${String(readBack.criteria.length)} criteria read back, not ${String(planned.criteria.length)}`);
    if (!sameNotes(readBack.notes, planned.text))
        out.push("the report text read back is not the text planned");
    return { matches: out.length === 0, differences: out };
}
/* -- 9. The batch: every ready official, one Confirm, one save at a time (2 Oct 2026) ---------- */
/**
 * THE REPORT BATCH - the Technical Delegate's request of 2 October 2026: sixteen technical-panel
 * officials (three TOs and thirteen Judges, every report drafted and every mark set) still to push,
 * the tournament ending the next day, and ONE action wanted instead of sixteen.
 *
 * The batch adds nothing to what one press does. Each official is still an ordinary report press -
 * its own press id, its own recorded plan, its own run of {@link pushReportDraft} (both indexes and
 * the form read fresh, ONE post with Save Draft, the read-back), its own result and its own pair of
 * receipts. What the batch adds is the sequence: one Confirm in the extension's window, then the
 * officials one after another, never two at once, a polite gap between one official's save and the
 * next official's first request (TMS has been slow under load), and - unlike the day push, which
 * stops after a save that did not read back - it CONTINUES past a failed official. A report press
 * changes one official's own mark and nothing anyone else's save depends on, so one official's
 * failure is no reason to hold back the rest; it is recorded in red, in its own words, and the
 * next official goes.
 */
export const TMS_REPORT_BATCH_LIMITS = {
    /** Officials in one batch: the whole roster's forty-eight, and no more. */
    presses: 48,
    /**
     * The polite gap, in milliseconds, between one official's save and the next official's first
     * request. The TD asked for at least two seconds; three are given.
     */
    gapMs: 3_000,
    /**
     * A batch press's hold: fifteen minutes, migration 069's own bound (`hold_until <= pressed_at +
     * 15 minutes`). A single report press keeps its ten. Sixteen officials, each six requests to a
     * TMS that has been slow, plus the gaps and the time to read the window, do not fit the ten - and
     * a report press holds nothing for another writer to respect (the migration's own words), so the
     * hold only bounds how long the plan may still be confirmed and sent.
     */
    holdMs: 15 * 60_000,
};
/** Our own core's words for a save TMS holds as Mark All ({@link pushReportDraft}). */
const MARK_ALL_WORDS = /TMS holds the mark as Mark All/;
function batchLine(entry) {
    const who = `${entry.subject.mediaName} (${entry.subject.role})`;
    const result = entry.result;
    if (result === null) {
        return {
            who,
            saved: false,
            words: "NOT confirmed as saved: no result for this official reached here. Check the official's marking in TMS by eye before pushing again.",
        };
    }
    if (result.outcome === "accepted_draft") {
        const target = result.target;
        const where = target === null
            ? ""
            : target.mode === "create"
                ? ` - a new mark${target.markId === null ? "" : ` (${target.markId})`} in competition ${target.competitionId}`
                : ` - mark ${target.markId ?? ""} in competition ${target.competitionId} updated, not duplicated`;
        const markAll = MARK_ALL_WORDS.test(result.detail) ? " TMS holds it as Mark All - the whole tournament." : "";
        return { who, saved: true, words: `Saved in TMS as a draft, and checked by reading it back${where}.${markAll}` };
    }
    if (result.cancelled || result.postsMade === 0)
        return { who, saved: false, words: `NOT saved - nothing was sent to TMS for this official: ${result.detail}` };
    return { who, saved: false, words: `NOT confirmed as saved: ${result.detail}` };
}
/**
 * A batch's closing summary - exactly what happened, official by official: N saved as drafts (each
 * read back as planned - nothing else counts as saved), M not, and why, in each result's own words.
 * The extension's window reads its results with this; the Control Centre's page, which counts by
 * what the API RECORDED (the API alone decides an acceptance), uses the same headline
 * ({@link reportBatchHeadline}), so the two never word one batch differently.
 */
export function summariseReportBatch(entries) {
    const lines = entries.map(batchLine);
    const saved = lines.filter((line) => line.saved).length;
    const failed = lines.length - saved;
    return { saved, failed, headline: reportBatchHeadline(saved, failed), lines };
}
/**
 * A batch's headline: "16 saved in TMS as drafts, each checked by reading it back." or "14 saved in
 * TMS as drafts. 2 NOT saved - each named below, with why." The Control Centre's page, which counts
 * by what the API recorded, says it in these same words.
 */
export function reportBatchHeadline(saved, failed) {
    const savedWords = `${String(saved)} saved in TMS as ${saved === 1 ? "a draft" : "drafts"}`;
    return failed === 0
        ? `${savedWords}, each checked by reading it back.`
        : `${savedWords}. ${String(failed)} NOT saved - ${failed === 1 ? "named below, with why" : "each named below, with why"}.`;
}
//# sourceMappingURL=tms-marking-push.js.map